import dynamic from 'next/dynamic';
import { findSubsidyTag } from '@lambda/reebelo/src/tagHelpers';
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import cn from 'classnames';
import { Dispatch, SetStateAction } from 'react';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import MainPriceInfo from '@/components/products/MainPriceInfo';
import PromoMessages from './PromoMessages';
import PurchaseLinks from '@/components/products/PurchaseLinks';
import BottomBar from '@/components/products/BottomBar';
import Usps from '../Usps';
import { ReviewSummaryT } from '@/settings/types';
import { Breadcrumb, ShippingData } from './types';
import { DeliveryDateResponse } from '@/api/products/delivery-date';
import {
  ReplacedProduct,
  SelectionT,
} from '@/lib/collections/catalogue/helpers';
import CompactTradeIn from './TradeIn/CompactTradeIn';
import BrandBadge from './BrandBadge';
import SustainableBadge from './SustainableBadge';

const Wishlist = dynamic(() => import('@/components/products/Wishlist'), {
  ssr: false,
});

const ReviewsStars = dynamic(
  () => import('@/components/collections/reviews/ReviewsStars'),
  {
    ssr: false,
  },
);

type ProductInfoSectionProps = {
  productName: string;
  productDetails: string;
  productReviewsSummary: ReviewSummaryT;
  totalPrice: number;
  comparePrice: number | undefined;
  offer: ReplacedProduct;
  shouldShowPromoMessage: boolean;
  category: string;
  purchaseLinkOffer: ReplacedProduct;
  product: PimProductT;
  warrantyID: number | null;
  shippingData: ShippingData;
  deliveryDate: DeliveryDateResponse | undefined;
  selection: SelectionT;
  isUnfitProduct: boolean;
  showTradeInModal: boolean;
  setShowTradeInModal: Dispatch<SetStateAction<boolean>>;
  isTradeInEligible: boolean;
  categoryWarrantyInfo?: ProductCategoryWarranty | null;
  categoryPk: string;
  isCertifiedVendor: boolean;
  origin: string | undefined;
  breadcrumb: Breadcrumb;
};

const ProductInfoSection = (props: ProductInfoSectionProps) => {
  const {
    productName,
    productDetails,
    productReviewsSummary,
    totalPrice,
    comparePrice,
    offer,
    shouldShowPromoMessage,
    category,
    purchaseLinkOffer,
    product,
    warrantyID,
    shippingData,
    deliveryDate,
    selection,
    isUnfitProduct,
    setShowTradeInModal,
    isTradeInEligible,
    categoryWarrantyInfo,
    categoryPk,
    isCertifiedVendor,
    origin,
    breadcrumb,
  } = props;

  return (
    <div className="mb-4 rounded-md border-gray-200 p-0 lg:border lg:p-4">
      <div className="flex items-center gap-3 lg:items-start">
        <div className="flex-1">
          {/* Product Tile */}
          <div
            id="e2e-product-name"
            className="flex-1 text-base font-extrabold leading-6 xs:text-lg lg:text-xl"
          >
            <h1 className="inline">{productName}</h1>
            {productDetails && <span> - {productDetails}</span>}
          </div>
          {/* Reviews Stars */}
          <ReviewsStars
            summary={productReviewsSummary}
            className="mb-1 text-xs"
            scrollBy="reviews-slider"
          />
          <div className="mt-2 flex lg:hidden">
            {isCertifiedVendor ? <BrandBadge /> : <SustainableBadge />}
          </div>
        </div>
        {/* Price Info Large Screens */}
        <div className="hidden lg:block">
          <MainPriceInfo
            actualPrice={totalPrice}
            comparePrice={comparePrice}
            isTradeInEligible={isTradeInEligible}
          />
        </div>
        {/* Wishlist Smaller Screens  */}
        <div className="w-[90px] lg:hidden">
          <Wishlist offer={offer} reviews={productReviewsSummary} />
        </div>
      </div>
      <div
        className={cn('hidden lg:block', {
          hidden: !(shouldShowPromoMessage || isTradeInEligible),
          'mt-5 mb-2': shouldShowPromoMessage || isTradeInEligible,
          'grid-cols-2 items-center lg:grid': isTradeInEligible,
        })}
      >
        <CompactTradeIn
          setShowTradeInModal={setShowTradeInModal}
          eligible={isTradeInEligible}
        />
        <div
          className={cn('hidden', {
            'lg:block': shouldShowPromoMessage,
          })}
        >
          <PromoMessages
            price={totalPrice}
            type="new-pdp"
            category={category}
            sku={offer?.sku}
          />
        </div>
      </div>
      <div
        className={cn('hidden w-full items-center gap-3 lg:flex', {
          'lg:mt-4': !(shouldShowPromoMessage || isTradeInEligible),
        })}
        id="purchaseCont"
      >
        <PurchaseLinks
          offer={purchaseLinkOffer}
          vendor={purchaseLinkOffer.vendor}
          category={product.category}
          warranty={warrantyID}
          shippingData={shippingData}
          deliverBy={{
            min: deliveryDate?.standard.minDeliveryDate || '',
            max: deliveryDate?.standard.maxDeliveryDate || '',
          }}
          subsidyTag={findSubsidyTag(purchaseLinkOffer.tags)}
          productId={purchaseLinkOffer.productId}
          variantId={purchaseLinkOffer.variantId}
          key={`${purchaseLinkOffer.productId}#${purchaseLinkOffer.vendor}`}
          cartButtonVariant="dark"
          upgradation={
            purchaseLinkOffer.replacedFromCondition
              ? `${selection?.condition} to ${purchaseLinkOffer.replacedFromCondition}`
              : undefined
          }
          comparePrice={comparePrice}
          className="grid flex-1 grid-cols-1 gap-1.5 py-0.5"
          isOutOfStock={isUnfitProduct}
          categoryPk={categoryPk}
        />
        <div className="w-[118px]">
          <Wishlist offer={offer} reviews={productReviewsSummary} />
        </div>
      </div>
      <BottomBar
        comparePrice={comparePrice}
        purchaseLinkOffer={purchaseLinkOffer}
        product={product}
        warrantyID={warrantyID}
        shippingData={shippingData}
        deliveryDate={deliveryDate}
        selection={selection}
        isUnfitProduct={isUnfitProduct}
        actualPrice={totalPrice}
        isTradeInEligible={isTradeInEligible}
        categoryPk={categoryPk}
      />
      <div className="mt-3 hidden lg:block">
        <Usps
          deliveryDate={deliveryDate}
          productCondition={selection.condition ?? ''}
          categoryWarrantyInfo={categoryWarrantyInfo}
          origin={origin}
          breadcrumb={breadcrumb}
        />
      </div>
    </div>
  );
};

export default ProductInfoSection;
